import PriceUpchargeTypeEnum from "../models/PriceUpchargeTypeEnum";
import PromoCodeValidationReasonEnum from "../models/PromoCodeValidationReasonEnum";

/**
 * Pure helpers for the promo code feature. No sockets, no contexts: the components own the
 * transport and hand values in / take results out so the rules here stay unit-testable.
 *
 * Discount math mirrors the API's PromoCodeService.computePromoDiscount / clampPromoDiscount so the
 * client-side totals recompute (OrderEntryService.calculateTotals) never drifts from the server.
 */

/** Admin-entered codes: 3-32 chars, upper-case letters, digits, dash or underscore (after normalizeCode). */
export const PROMO_CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

export interface IPromoCodeFormValues {
    name: string;
    code: string;
    discount_type_id: PriceUpchargeTypeEnum | number | string;
    discount_value: number | string;
    min_order_subtotal?: number | string | null;
    max_uses?: number | string | null;
    single_use_per_customer: boolean;
    start_date?: string | Date | null;
    end_date?: string | Date | null;
    active: boolean;
}

export type IPromoCodeFormErrors = Partial<Record<keyof IPromoCodeFormValues, string>>;

/** Trim + upper-case, matching the API's normalizeCode so what the user typed is what gets looked up. */
const normalizeCode = (code: string | null | undefined): string => {
    return (code || "").trim().toUpperCase();
};

const isValidCodeFormat = (code: string | null | undefined): boolean => {
    return PROMO_CODE_PATTERN.test(normalizeCode(code));
};

const isBlank = (value: unknown): boolean => {
    return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
};

/**
 * Formik-style validation for the admin create / edit form. Returns translation keys per field.
 * `timesUsed` guards against lowering max_uses below the redemptions already recorded.
 */
const validatePromoCodeForm = (values: IPromoCodeFormValues, opts?: { timesUsed?: number }): IPromoCodeFormErrors => {
    const errors: IPromoCodeFormErrors = {};

    if (!values.name || !values.name.trim().length) {
        errors.name = "required";
    }

    const code = normalizeCode(values.code);

    if (!code.length) {
        errors.code = "required";
    } else if (!PROMO_CODE_PATTERN.test(code)) {
        errors.code = "promo_code_format_help";
    }

    const discountTypeId = Number(values.discount_type_id);

    if (discountTypeId !== PriceUpchargeTypeEnum.FIXED && discountTypeId !== PriceUpchargeTypeEnum.PERCENT) {
        errors.discount_type_id = "required";
    }

    const discountValue = Number(values.discount_value);

    if (isBlank(values.discount_value) || Number.isNaN(discountValue) || discountValue <= 0) {
        errors.discount_value = "promo_code_discount_value_positive";
    } else if (discountTypeId === PriceUpchargeTypeEnum.PERCENT && discountValue > 100) {
        errors.discount_value = "promo_code_percent_max";
    }

    if (!isBlank(values.min_order_subtotal)) {
        const minSubtotal = Number(values.min_order_subtotal);

        if (Number.isNaN(minSubtotal) || minSubtotal < 0) {
            errors.min_order_subtotal = "promo_code_min_subtotal_invalid";
        }
    }

    if (!isBlank(values.max_uses)) {
        const maxUses = Number(values.max_uses);

        if (!Number.isInteger(maxUses) || maxUses < 1) {
            errors.max_uses = "promo_code_max_uses_invalid";
        } else if (opts?.timesUsed && maxUses < opts.timesUsed) {
            errors.max_uses = "promo_code_max_uses_below_times_used";
        }
    }

    if (values.start_date && values.end_date) {
        const start = new Date(values.start_date).getTime();
        const end = new Date(values.end_date).getTime();

        if (!Number.isNaN(start) && !Number.isNaN(end) && end < start) {
            errors.end_date = "promo_code_end_before_start";
        }
    }

    return errors;
};

/** Translation key for a validation failure reason (`promo_code_error_<reason>`). */
const getErrorMessageKey = (reason: PromoCodeValidationReasonEnum | string | null | undefined): string => {
    const known = Object.values(PromoCodeValidationReasonEnum) as string[];

    if (reason && known.includes(reason)) {
        return `promo_code_error_${reason}`;
    }

    return "promo_code_error_invalid";
};

const roundTo = (value: number, precision: number): number => {
    const factor = Math.pow(10, precision);

    return Math.round((value + Number.EPSILON) * factor) / factor;
};

/**
 * Discount for a subtotal. FIXED is capped at the subtotal, PERCENT is value% of it, never negative.
 * Mirrors the API's PromoCodeService.computePromoDiscount.
 */
const computePromoDiscount = (
    discountTypeId: PriceUpchargeTypeEnum | number | null | undefined,
    discountValue: number | string | null | undefined,
    subtotal: number,
    precision = 2
): number => {
    const value = Number(discountValue) || 0;
    const base = Number(subtotal) || 0;

    if (value <= 0 || base <= 0) {
        return 0;
    }

    let discount = 0;

    if (Number(discountTypeId) === PriceUpchargeTypeEnum.FIXED) {
        discount = Math.min(value, base);
    } else if (Number(discountTypeId) === PriceUpchargeTypeEnum.PERCENT) {
        discount = (base * value) / 100;
    }

    return Math.max(0, roundTo(discount, precision));
};

/** The manual discount and the promo together never exceed the subtotal (mirrors the API clamp). */
const clampPromoDiscount = (subtotal: number, orderDiscount: number, promoDiscount: number): number => {
    const base = Number(subtotal) || 0;
    const manual = Number(orderDiscount) || 0;
    const promo = Number(promoDiscount) || 0;

    if (manual + promo > base) {
        return Math.max(0, base - manual);
    }

    return Math.max(0, promo);
};

/** "10%" or "$5.00" for list / summary display. */
const formatDiscountLabel = (
    discountTypeId: PriceUpchargeTypeEnum | number | null | undefined,
    discountValue: number | string | null | undefined,
    precision = 2
): string => {
    const value = Number(discountValue) || 0;

    if (Number(discountTypeId) === PriceUpchargeTypeEnum.PERCENT) {
        return `${roundTo(value, precision)}%`;
    }

    return `$${value.toLocaleString("en-US", { minimumFractionDigits: precision, maximumFractionDigits: precision })}`;
};

/**
 * Whether a Notify payload from the API is a promo code failure (`ERROR_PROMO_CODE_<REASON>` from a
 * checkout choke point, or `ERROR_PROMO_CODE_*` from the promo endpoints).
 */
const isPromoCodeNotifyMessage = (message: string | null | undefined): boolean => {
    return typeof message === "string" && message.toUpperCase().startsWith("ERROR_PROMO_CODE_");
};

const PromoCodeService = {
    normalizeCode,
    isValidCodeFormat,
    validatePromoCodeForm,
    getErrorMessageKey,
    computePromoDiscount,
    clampPromoDiscount,
    formatDiscountLabel,
    isPromoCodeNotifyMessage,
};

export default PromoCodeService;
